import { AllInclusiveCmsItem, OBSObjI } from '@msc-b2c/interfaces/types';
import { OBSTypesEnum } from '@msc-b2c/interfaces/enums';

const unique = (codes: Array<string | undefined | null>) =>
  Array.from(new Set(codes.filter((c): c is string => Boolean(c))));

const getItemRelatedCodes = (item?: AllInclusiveCmsItem): string[] =>
  unique([...(item?.adultRelatedServiceCodes ?? []), ...(item?.minorsRelatedServiceCodes ?? [])]);

export const getCmsAllInclusiveItemByCode = (
  items: AllInclusiveCmsItem[] | undefined,
  code?: string
): AllInclusiveCmsItem | undefined => {
  if (!code || !items?.length) return undefined;
  return items.find((item) => item.code === code);
};

export const isCmsConfiguredAllInclusive = (
  items: AllInclusiveCmsItem[] | undefined,
  code?: string
): boolean => Boolean(getCmsAllInclusiveItemByCode(items, code)?.isAllInclusive);

export const isAllInclusiveByCmsOrTypeCode = (
  items: AllInclusiveCmsItem[] | undefined,
  code?: string,
  typeCode?: string
): boolean => isCmsConfiguredAllInclusive(items, code) || typeCode === OBSTypesEnum.BEVERAGES;

export const getCmsRelatedCodes = (
  items: AllInclusiveCmsItem[] | undefined,
  code?: string
): string[] => {
  if (!code || !items?.length) return [];
  const directItem = getCmsAllInclusiveItemByCode(items, code);
  const directCodes = getItemRelatedCodes(directItem);
  const reverseCodes = items
    .filter((item) => {
      if (item.code === code) return false;
      return getItemRelatedCodes(item).includes(code);
    })
    .map((item) => item.code);

  return unique([code, ...directCodes, ...reverseCodes]).filter(
    (relatedCode) => relatedCode !== code
  );
};

/** OBS package linked via prepaid service codes (not only CMS all-inclusive config). */
export const isCombinedObsPackage = (
  card: OBSObjI | null | undefined,
  onboardServices: OBSObjI[] | undefined,
  allInclusiveCmsItems: AllInclusiveCmsItem[] | undefined
): boolean => {
  const cardCode = card?.code;
  if (!cardCode) {
    return false;
  }

  const hasDirectRelatedCodes =
    (card.prepaidItems?.serviceCodes?.adults?.length ?? 0) > 0 ||
    (card.prepaidItems?.serviceCodes?.minors?.length ?? 0) > 0 ||
    getCmsRelatedCodes(allInclusiveCmsItems, cardCode).length > 0;

  if (hasDirectRelatedCodes) {
    return true;
  }

  return (onboardServices ?? []).some((item) => {
    if (item.code === cardCode) {
      return false;
    }

    const relatedCodes = [
      ...(item.prepaidItems?.serviceCodes?.adults ?? []),
      ...(item.prepaidItems?.serviceCodes?.minors ?? []),
    ];

    return relatedCodes.includes(cardCode);
  });
};

export const isAllInclusiveOrCombinedObsRefund = (
  card: OBSObjI | null | undefined,
  allInclusiveCmsItems: AllInclusiveCmsItem[] | undefined,
  onboardServices: OBSObjI[] | undefined
): boolean =>
  Boolean(card?.prepaidItems?.isAllInclusive) ||
  isAllInclusiveByCmsOrTypeCode(allInclusiveCmsItems, card?.code, card?.type?.code) ||
  getCmsRelatedCodes(allInclusiveCmsItems, card?.code).length > 0 ||
  isCombinedObsPackage(card, onboardServices, allInclusiveCmsItems);

/** True when the OBS is all-inclusive via its prepaid flag, CMS config or BEVERAGES type. */
export const isAllInclusiveObs = (
  card: OBSObjI | null | undefined,
  allInclusiveCmsItems: AllInclusiveCmsItem[] | undefined
): boolean =>
  Boolean(card?.prepaidItems?.isAllInclusive) ||
  isAllInclusiveByCmsOrTypeCode(allInclusiveCmsItems, card?.code, card?.type?.code);

/**
 * Applicable to minors only (child + junior, never adult/senior).
 * Mirrors `isPerChildService` in PostBookingResultsUtilities; inlined here to keep this
 * module a dependency-light leaf.
 */
const isMinorOnlyApplicable = (card: OBSObjI | null | undefined): boolean =>
  Boolean(
    card?.applicability?.isApplicableForChild &&
      card?.applicability?.isApplicableForJunior &&
      !card?.applicability?.isApplicableForAdult &&
      !card?.applicability?.isApplicableForSenior
  );

/**
 * A minor ALLINC package: all-inclusive AND priced for minors only (no adult price).
 * These are never shown as their own card in results — only as part of the linked adult card.
 */
export const isMinorOnlyAllInclusiveObs = (
  card: OBSObjI | null | undefined,
  allInclusiveCmsItems: AllInclusiveCmsItem[] | undefined
): boolean => isAllInclusiveObs(card, allInclusiveCmsItems) && isMinorOnlyApplicable(card);

/**
 * Resolves the minor OBS linked to an adult ALLINC card, via the adult's prepaid
 * `serviceCodes.minors` and the CMS `minorsRelatedServiceCodes`. Returns undefined when
 * no linkage exists (no-linkage scenario — adult shows/adds adults only).
 */
export const getLinkedMinorObs = (
  adultCard: OBSObjI | null | undefined,
  onboardServices: OBSObjI[] | undefined,
  allInclusiveCmsItems: AllInclusiveCmsItem[] | undefined
): OBSObjI | undefined => {
  if (!adultCard?.code) {
    return undefined;
  }

  const minorCodes = unique([
    ...(adultCard.prepaidItems?.serviceCodes?.minors ?? []),
    ...(getCmsAllInclusiveItemByCode(allInclusiveCmsItems, adultCard.code)
      ?.minorsRelatedServiceCodes ?? []),
  ]);

  if (!minorCodes.length) {
    return undefined;
  }

  return (onboardServices ?? []).find((obs) => !!obs.code && minorCodes.includes(obs.code));
};

/**
 * True when this OBS must be hidden as its own card and shown only merged into the linked adult
 * card — i.e. it is the minor half of an adult+minor ALLINC package. Detected either by it being
 * priced/applicable for minors only ({@link isMinorOnlyAllInclusiveObs}) or, more robustly, by its
 * code being referenced as a minor by another all-inclusive package (via prepaid
 * `serviceCodes.minors` or CMS `minorsRelatedServiceCodes`). The reverse-linkage check matters in
 * contexts (e.g. already-purchased) where the booking-derived item lacks full price/applicability
 * data but the linked adult package still carries the linkage codes.
 */
export const isHiddenMinorObs = (
  card: OBSObjI | null | undefined,
  onboardServices: OBSObjI[] | undefined,
  allInclusiveCmsItems: AllInclusiveCmsItem[] | undefined
): boolean => {
  if (isMinorOnlyAllInclusiveObs(card, allInclusiveCmsItems)) {
    return true;
  }

  const code = card?.code;
  if (!code) {
    return false;
  }

  const referencedByAllInclusiveObs = (onboardServices ?? []).some(
    (item) =>
      item.code !== code &&
      isAllInclusiveObs(item, allInclusiveCmsItems) &&
      (item.prepaidItems?.serviceCodes?.minors ?? []).includes(code)
  );
  if (referencedByAllInclusiveObs) {
    return true;
  }

  return (allInclusiveCmsItems ?? []).some(
    (item) => item.code !== code && (item.minorsRelatedServiceCodes ?? []).includes(code)
  );
};
